/**
 * Header/magic-byte file-type detection for the pricelist upload flow.
 *
 * Detection is driven by the file's leading bytes (and, for ZIP-based Office
 * documents, by the entry names embedded in the archive) rather than by the
 * file extension or a client-supplied MIME type. Both the frontend (validating
 * a selected file before upload) and the backend (routing an uploaded buffer to
 * the correct extraction handler) share this logic so the two agree on what a
 * file is.
 */

import { readU16LE, readU32LE } from "./byte-reading";
import {
  detectOle2DirectoryKind,
  detectOle2DirectoryKindFromBytes,
  hasOle2Magic,
  type Ole2DirectoryKind,
} from "./ole2-cfb";

export type PricelistFileKind =
  | "pdf"
  | "excel"
  | "image"
  | "csv"
  | "xml"
  | "word"
  /**
   * An Outlook `.msg` — the vendor's price change NOTICE as the email itself
   * (GEN-2521). Stored and attached to a case like any other notice; never
   * extracted, since an email body is not a price table.
   */
  | "email";

export interface DetectedFileType {
  kind: PricelistFileKind;
  mimeType: string;
}

/** MIME types accepted for the "image" kind, keyed for reuse in UI copy. */
export const SUPPORTED_IMAGE_MIME_TYPES = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "image/bmp",
  "image/svg+xml",
] as const;

export const PDF_MIME_TYPE = "application/pdf";
export const XLSX_MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
export const XLS_MIME_TYPE = "application/vnd.ms-excel";
/** Excel Binary Workbook — a ZIP of `.bin` parts, not the XML XLSX ExcelJS streams. */
export const XLSB_MIME_TYPE =
  "application/vnd.ms-excel.sheet.binary.macroEnabled.12";
/** Delimited text of any flavour — a `.txt` of tab-separated rows is this too. */
export const CSV_MIME_TYPE = "text/csv";
export const TEXT_PLAIN_MIME_TYPE = "text/plain";
export const XML_MIME_TYPE = "application/xml";
export const DOC_MIME_TYPE = "application/msword";
export const DOCX_MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
/** Outlook `.msg` (CFB / MS-OXMSG). Classic Outlook hands the browser an EMPTY `File.type` for these. */
export const MSG_MIME_TYPE = "application/vnd.ms-outlook";

/**
 * Every MIME type that can be the source file of a pricelist.
 *
 * `text/csv` is both a source type and the type of every *extracted* CSV, so
 * MIME type alone no longer tells the two apart. The discriminator is
 * `File.parentFileId`: a source file has no parent, an extracted CSV always
 * records the source as its parent. Queries that mean "source pricelists" must
 * filter on that, not on this list.
 */
export const SUPPORTED_PRICELIST_SOURCE_MIME_TYPES = [
  PDF_MIME_TYPE,
  XLSX_MIME_TYPE,
  XLS_MIME_TYPE,
  XLSB_MIME_TYPE,
  CSV_MIME_TYPE,
  TEXT_PLAIN_MIME_TYPE,
  XML_MIME_TYPE,
  "text/xml",
  DOC_MIME_TYPE,
  DOCX_MIME_TYPE,
  MSG_MIME_TYPE,
  ...SUPPORTED_IMAGE_MIME_TYPES,
] as const;

/**
 * Every MIME type a vendor EDI implementation guideline may be uploaded as:
 * spreadsheets and PDFs.
 *
 * Vendors send one or the other — Square D's 4010 845 guideline is a
 * spreadsheet, several others publish PDFs. Lives here rather than in either
 * workspace because three places have to agree on it: the file picker's
 * `accept`, the presign-time refusal, and the check against the stored blob's
 * own header bytes.
 */
export const EDI_GUIDELINE_MIME_TYPES = [
  XLSX_MIME_TYPE,
  XLS_MIME_TYPE,
  PDF_MIME_TYPE,
] as const;

export function isEdiGuidelineMimeType(mimeType: string): boolean {
  return (EDI_GUIDELINE_MIME_TYPES as readonly string[]).includes(mimeType);
}

/**
 * The MIME type implied by a guideline's file extension.
 *
 * Needed because a browser's `File.type` is not dependable for spreadsheets: on
 * a machine with no application registered for `.xls`/`.xlsx` it comes through
 * empty, and the presign check would then refuse a file the converter reads
 * perfectly well. The extension is the fallback, never the override — the
 * stored blob's header bytes still decide what the file actually is.
 */
export function ediGuidelineMimeTypeFromFileName(
  fileName: string,
): string | null {
  const match = /\.([A-Za-z0-9]+)$/.exec(fileName);
  switch (match?.[1]?.toLowerCase()) {
    case "xlsx":
      return XLSX_MIME_TYPE;
    case "xls":
      return XLS_MIME_TYPE;
    case "pdf":
      return PDF_MIME_TYPE;
    default:
      return null;
  }
}

/**
 * Upload ceiling per pricelist file kind, shared by the frontend validator and
 * the backend's post-upload check so both enforce the same number.
 *
 * The split reflects what each format costs to extract: delimited text and
 * spreadsheets stream through the schema-map engine without a model call per
 * row, so their ceiling is the GEN-2321 target. PDFs and images still cost one
 * model call per page batch / image band, so their caps are a cost and latency
 * decision, not a technical one.
 */
export const PRICELIST_UPLOAD_MAX_BYTES: Record<PricelistFileKind, number> = {
  csv: 1024 * 1024 * 1024, // 1 GB
  excel: 1024 * 1024 * 1024, // 1 GB — XLSX streams; XLSB is capped separately
  pdf: 100 * 1024 * 1024, // 100 MB
  xml: 100 * 1024 * 1024, // 100 MB — held in memory for the model
  word: 100 * 1024 * 1024, // 100 MB — same as PDF; extraction is a model call
  image: 25 * 1024 * 1024, // 25 MB
  email: 100 * 1024 * 1024, // 100 MB — stored only, never extracted; attachments ride inside
};

/**
 * Ceiling for workbooks that SheetJS has to hold in memory: binary `.xlsb`
 * (ExcelJS cannot stream it) and the whole-file fallback when the XLSX
 * streaming reader dies. A Rockwell Canada price list is ~44 MB and must
 * still extract; the 1 GB excel-kind cap must not, because that parse would
 * OOM the extraction process (GEN-2394).
 */
export const PRICELIST_SHEETJS_IN_MEMORY_MAX_BYTES = 80 * 1024 * 1024;

/** Same number as {@link PRICELIST_SHEETJS_IN_MEMORY_MAX_BYTES}, named for the upload gate. */
export const PRICELIST_XLSB_UPLOAD_MAX_BYTES =
  PRICELIST_SHEETJS_IN_MEMORY_MAX_BYTES;

function formatMb(bytes: number): number {
  return Math.max(1, Math.ceil(bytes / (1024 * 1024)));
}

/** User-facing refusal when a `.xlsb` exceeds {@link PRICELIST_XLSB_UPLOAD_MAX_BYTES}. */
export function pricelistXlsbTooLargeMessage(fileSizeBytes: number): string {
  return `Binary Excel (.xlsb) files larger than ${formatMb(PRICELIST_XLSB_UPLOAD_MAX_BYTES)} MB cannot be extracted in memory (this file is ${formatMb(fileSizeBytes)} MB). Save the workbook as .xlsx or .csv and upload that instead.`;
}

/**
 * User-facing refusal when any SheetJS whole-file parse would exceed
 * {@link PRICELIST_SHEETJS_IN_MEMORY_MAX_BYTES}.
 */
export function pricelistSheetJsTooLargeMessage(fileSizeBytes: number): string {
  return `This workbook is too large to parse in memory (${formatMb(fileSizeBytes)} MB; limit ${formatMb(PRICELIST_SHEETJS_IN_MEMORY_MAX_BYTES)} MB). Save it as a standard .xlsx or .csv and upload that instead.`;
}

/** The largest cap of any kind — the presign-time gate, before bytes exist to detect a kind from. */
export const PRICELIST_UPLOAD_MAX_BYTES_ANY_KIND = Math.max(
  ...Object.values(PRICELIST_UPLOAD_MAX_BYTES),
);

/**
 * How many leading bytes {@link detectFileTypeFromSample} needs. Covers every
 * magic number, the SVG scan window, and the delimited-text sample.
 */
export const PRICELIST_DETECTION_SAMPLE_BYTES = 64 * 1024;

function startsWith(bytes: Uint8Array, sig: number[], offset = 0): boolean {
  if (bytes.length < offset + sig.length) return false;
  for (let i = 0; i < sig.length; i++) {
    if (bytes[offset + i] !== sig[i]) return false;
  }
  return true;
}

/** Locate an ASCII substring within a byte array. Returns true if present. */
function containsAscii(
  bytes: Uint8Array,
  needle: string,
  limit?: number,
): boolean {
  const end = limit != null ? Math.min(bytes.length, limit) : bytes.length;
  const first = needle.charCodeAt(0);
  outer: for (let i = 0; i <= end - needle.length; i++) {
    if (bytes[i] !== first) continue;
    for (let j = 1; j < needle.length; j++) {
      if (bytes[i + j] !== needle.charCodeAt(j)) continue outer;
    }
    return true;
  }
  return false;
}

// ZIP archives store each entry's path as plaintext in its local file header,
// so an xlsx/xlsm/xlsb workbook can be recognised by the "xl/workbook" entry
// without decompressing anything. Word (.docx) is "word/document"; PowerPoint
// and plain zips have neither and are rejected.
function zipIsExcelWorkbook(bytes: Uint8Array): boolean {
  return containsAscii(bytes, "xl/workbook");
}

const ZIP_CENTRAL_HEADER = [0x50, 0x4b, 0x01, 0x02];
const ZIP_EOCD_HEADER = [0x50, 0x4b, 0x05, 0x06];
const ZIP_LOCAL_HEADER_SIZE = 30;
const ZIP_CENTRAL_HEADER_SIZE = 46;
const ZIP_EOCD_SIZE = 22;
/** EOCD plus the longest legal ZIP comment — enough tail to always find the directory. */
export const ZIP_EOCD_SCAN_BYTES = ZIP_EOCD_SIZE + 0xffff;
const MAX_CENTRAL_DIRECTORY_BYTES = 8 * 1024 * 1024;
const MAX_LOCAL_HEADER_WALK = 20_000;
const XLSB_WORKBOOK_ENTRY = "xl/workbook.bin";
const XLSX_WORKBOOK_ENTRY = "xl/workbook.xml";

function asciiSliceEquals(
  bytes: Uint8Array,
  offset: number,
  length: number,
  needle: string,
): boolean {
  if (length !== needle.length || offset + length > bytes.length) return false;
  for (let i = 0; i < length; i++) {
    if (bytes[offset + i] !== needle.charCodeAt(i)) return false;
  }
  return true;
}

/** End-of-central-directory offset, searching backward through a bounded slice. */
function findEocdOffset(bytes: Uint8Array): number | null {
  if (bytes.length < ZIP_EOCD_SIZE) return null;
  const earliest = Math.max(0, bytes.length - ZIP_EOCD_SIZE - 0xffff);
  for (let i = bytes.length - ZIP_EOCD_SIZE; i >= earliest; i--) {
    if (startsWith(bytes, ZIP_EOCD_HEADER, i)) return i;
  }
  return null;
}

function centralDirectoryStart(
  bytes: Uint8Array,
  eocd: number,
  cdSize: number,
  cdOffset: number,
): number | null {
  if (startsWith(bytes, ZIP_CENTRAL_HEADER, cdOffset)) return cdOffset;
  const inferred = eocd - cdSize;
  if (inferred < 0 || inferred >= eocd) return null;
  return inferred;
}

function nextCentralDirectoryOffset(
  bytes: Uint8Array,
  headerAt: number,
): number | null {
  const nameLen = readU16LE(bytes, headerAt + 28);
  const extraLen = readU16LE(bytes, headerAt + 30);
  const commentLen = readU16LE(bytes, headerAt + 32);
  if (nameLen == null || extraLen == null || commentLen == null) return null;
  return headerAt + ZIP_CENTRAL_HEADER_SIZE + nameLen + extraLen + commentLen;
}

function zipCentralDirectoryWalkHas(
  bytes: Uint8Array,
  cdStart: number,
  eocd: number,
  needle: string,
): boolean {
  let i = cdStart;
  while (
    i + ZIP_CENTRAL_HEADER_SIZE <= eocd &&
    startsWith(bytes, ZIP_CENTRAL_HEADER, i)
  ) {
    const nameLen = readU16LE(bytes, i + 28);
    if (nameLen == null) return false;
    if (asciiSliceEquals(bytes, i + ZIP_CENTRAL_HEADER_SIZE, nameLen, needle)) {
      return true;
    }
    const next = nextCentralDirectoryOffset(bytes, i);
    if (next == null || next <= i) return false;
    i = next;
  }
  return false;
}

/**
 * Walk ZIP central-directory filenames. Works on a full archive or a tail
 * slice that still holds the EOCD and the directory that precedes it.
 */
function zipCentralDirectoryHas(bytes: Uint8Array, needle: string): boolean {
  const eocd = findEocdOffset(bytes);
  if (eocd == null) return false;
  const cdSize = readU32LE(bytes, eocd + 12);
  const cdOffset = readU32LE(bytes, eocd + 16);
  if (cdSize == null || cdOffset == null) return false;
  const cdStart = centralDirectoryStart(bytes, eocd, cdSize, cdOffset);
  if (cdStart == null) return false;
  return zipCentralDirectoryWalkHas(bytes, cdStart, eocd, needle);
}

/** Walk local-file headers without decompressing payloads. */
function zipLocalHeadersHave(bytes: Uint8Array, needle: string): boolean {
  let i = 0;
  while (
    i + ZIP_LOCAL_HEADER_SIZE <= bytes.length &&
    startsWith(bytes, ZIP_LOCAL_HEADER, i)
  ) {
    const nameLen = readU16LE(bytes, i + 26);
    const extraLen = readU16LE(bytes, i + 28);
    const compSize = readU32LE(bytes, i + 18);
    if (nameLen == null || extraLen == null || compSize == null) return false;
    const nameAt = i + ZIP_LOCAL_HEADER_SIZE;
    if (asciiSliceEquals(bytes, nameAt, nameLen, needle)) return true;
    const next = nameAt + nameLen + extraLen + compSize;
    if (next <= i) return false;
    i = next;
  }
  return false;
}

/** XLSB stores `xl/workbook.bin` instead of `xl/workbook.xml`. */
export function zipLooksLikeXlsb(bytes: Uint8Array): boolean {
  return (
    zipLocalHeadersHave(bytes, XLSB_WORKBOOK_ENTRY) ||
    zipCentralDirectoryHas(bytes, XLSB_WORKBOOK_ENTRY) ||
    containsAscii(bytes, XLSB_WORKBOOK_ENTRY)
  );
}

/** Streaming XLSX proof — `xl/workbook` alone also matches `.bin`. */
export function zipLooksLikeXlsxXml(bytes: Uint8Array): boolean {
  return (
    zipLocalHeadersHave(bytes, XLSX_WORKBOOK_ENTRY) ||
    zipCentralDirectoryHas(bytes, XLSX_WORKBOOK_ENTRY) ||
    containsAscii(bytes, XLSX_WORKBOOK_ENTRY)
  );
}

function zipKindFromBuffers(...buffers: Uint8Array[]): "xlsb" | "xlsx" | null {
  for (const bytes of buffers) {
    if (bytes.length > 0 && zipLooksLikeXlsb(bytes)) return "xlsb";
  }
  for (const bytes of buffers) {
    if (bytes.length > 0 && zipLooksLikeXlsxXml(bytes)) return "xlsx";
  }
  return null;
}

/**
 * Absolute central-directory span from a tail that still holds the EOCD.
 * `tail` is the last `tail.length` bytes of a `fileSize`-byte archive.
 */
export function zipCentralDirectoryByteRange(
  tail: Uint8Array,
  fileSize: number,
): { offset: number; size: number } | null {
  const eocd = findEocdOffset(tail);
  if (eocd == null) return null;
  const cdSize = readU32LE(tail, eocd + 12);
  const cdOffset = readU32LE(tail, eocd + 16);
  if (cdSize == null || cdOffset == null || cdSize === 0) return null;
  if (cdSize > MAX_CENTRAL_DIRECTORY_BYTES) return null;
  if (cdOffset === 0xffffffff || cdOffset + cdSize > fileSize) return null;
  return { offset: cdOffset, size: cdSize };
}

function zipLocalHeaderSpan(header: Uint8Array): {
  nameLength: number;
  extraLength: number;
  compressedSize: number;
} | null {
  if (
    header.length < ZIP_LOCAL_HEADER_SIZE ||
    !startsWith(header, ZIP_LOCAL_HEADER)
  ) {
    return null;
  }
  const flags = readU16LE(header, 6);
  const nameLength = readU16LE(header, 26);
  const extraLength = readU16LE(header, 28);
  const compressedSize = readU32LE(header, 18);
  if (flags == null || nameLength == null || extraLength == null) return null;
  if (compressedSize == null) return null;
  if ((flags & 0x8) !== 0 && compressedSize === 0) return null;
  return { nameLength, extraLength, compressedSize };
}

export type ZipRangeReader = (
  offset: number,
  length: number,
) => Promise<Uint8Array>;

async function zipKindFromUnreadCentralDirectory(
  tail: Uint8Array,
  fileSize: number,
  readRange: ZipRangeReader,
): Promise<"xlsb" | "xlsx" | null> {
  const range = zipCentralDirectoryByteRange(tail, fileSize);
  if (range == null) return null;
  const tailStart = fileSize - tail.length;
  if (range.offset >= tailStart) return null;
  return zipKindFromBuffers(await readRange(range.offset, range.size));
}

function workbookKindFromZipName(
  nameBytes: Uint8Array,
  nameLength: number,
): "xlsb" | "xlsx" | null {
  if (asciiSliceEquals(nameBytes, 0, nameLength, XLSB_WORKBOOK_ENTRY)) {
    return "xlsb";
  }
  if (asciiSliceEquals(nameBytes, 0, nameLength, XLSX_WORKBOOK_ENTRY)) {
    return "xlsx";
  }
  return null;
}

function nextLocalHeaderOffset(
  offset: number,
  span: { nameLength: number; extraLength: number; compressedSize: number },
): number | null {
  const next =
    offset +
    ZIP_LOCAL_HEADER_SIZE +
    span.nameLength +
    span.extraLength +
    span.compressedSize;
  return next > offset ? next : null;
}

async function readNextZipLocalKind(
  fileSize: number,
  offset: number,
  readRange: ZipRangeReader,
): Promise<{ kind: "xlsb" | "xlsx" | null; nextOffset: number } | null> {
  if (offset + ZIP_LOCAL_HEADER_SIZE > fileSize) return null;
  const header = await readRange(offset, ZIP_LOCAL_HEADER_SIZE);
  const span = zipLocalHeaderSpan(header);
  if (span == null) return null;
  const nameAt = offset + ZIP_LOCAL_HEADER_SIZE;
  if (nameAt + span.nameLength > fileSize) return null;
  const next = nextLocalHeaderOffset(offset, span);
  if (next == null) return null;
  const nameBytes = await readRange(nameAt, span.nameLength);
  return {
    kind: workbookKindFromZipName(nameBytes, span.nameLength),
    nextOffset: next,
  };
}

async function zipKindByWalkingLocalHeaders(
  fileSize: number,
  readRange: ZipRangeReader,
): Promise<"xlsb" | "xlsx" | null> {
  let offset = 0;
  for (let i = 0; i < MAX_LOCAL_HEADER_WALK; i++) {
    const step = await readNextZipLocalKind(fileSize, offset, readRange);
    if (step == null) return null;
    if (step.kind) return step.kind;
    offset = step.nextOffset;
  }
  return null;
}

/**
 * Decide XLSB vs streaming XLSX when the head/tail samples are inconclusive.
 * `readRange` loads the central directory or walks local headers as needed.
 */
export async function detectZipExcelKind(input: {
  head: Uint8Array;
  tail?: Uint8Array;
  fileSize: number;
  readRange?: ZipRangeReader;
}): Promise<"xlsb" | "xlsx" | null> {
  const tail = input.tail ?? new Uint8Array();
  const fromSamples = zipKindFromBuffers(input.head, tail);
  if (fromSamples) return fromSamples;
  if (input.readRange == null) return null;
  const fromCd = await zipKindFromUnreadCentralDirectory(
    tail,
    input.fileSize,
    input.readRange,
  );
  if (fromCd) return fromCd;
  return zipKindByWalkingLocalHeaders(input.fileSize, input.readRange);
}

function zipIsWordDocument(bytes: Uint8Array): boolean {
  return containsAscii(bytes, "word/document");
}

// SVG has no binary magic number. Scan the leading bytes (skipping a UTF-8 BOM)
// for an "<svg" root element, which may follow an XML declaration, comments, or
// a DOCTYPE.
function looksLikeSvg(bytes: Uint8Array): boolean {
  const hasBom = startsWith(bytes, [0xef, 0xbb, 0xbf]);
  const scanStart = hasBom ? 3 : 0;
  const window = bytes.subarray(scanStart, scanStart + 2048);
  return containsAscii(window, "<svg") || containsAscii(window, "<SVG");
}

function skipBomAndWhitespace(bytes: Uint8Array): number {
  let i = startsWith(bytes, [0xef, 0xbb, 0xbf]) ? 3 : 0;
  while (i < bytes.length && isWhitespaceByte(bytes[i])) i++;
  return i;
}

/** Fold an ASCII letter to lowercase; leave every other byte unchanged. */
function foldAsciiLower(code: number): number {
  return code >= 0x41 && code <= 0x5a ? code + 0x20 : code;
}

function asciiStartsWithIgnoreCase(bytes: Uint8Array, prefix: string): boolean {
  if (bytes.length < prefix.length) return false;
  for (let i = 0; i < prefix.length; i++) {
    const actual = bytes[i];
    if (
      actual === undefined ||
      foldAsciiLower(actual) !== foldAsciiLower(prefix.charCodeAt(i))
    ) {
      return false;
    }
  }
  return true;
}

function looksLikeHtml(bytes: Uint8Array): boolean {
  const rest = bytes.subarray(skipBomAndWhitespace(bytes));
  return (
    asciiStartsWithIgnoreCase(rest, "<!doctype html") ||
    asciiStartsWithIgnoreCase(rest, "<html")
  );
}

/** First byte of an XML NameStartChar we care about: A–Z, a–z, or underscore. */
function isXmlNameStart(byte: number): boolean {
  return (
    (byte >= 0x41 && byte <= 0x5a) ||
    (byte >= 0x61 && byte <= 0x7a) ||
    byte === 0x5f
  );
}

/**
 * Markup that is not HTML or SVG — a vendor XML catalog, Word 2003 XML, or
 * any other `<?xml` / rooted-element document. SVG is claimed earlier; HTML
 * stays rejected so a dropped webpage is not uploaded as a pricelist.
 */
function looksLikeXml(bytes: Uint8Array): boolean {
  if (looksLikeSvg(bytes) || looksLikeHtml(bytes)) return false;
  const i = skipBomAndWhitespace(bytes);
  if (startsWith(bytes, [0x3c, 0x3f, 0x78, 0x6d, 0x6c], i)) return true; // <?xml
  const next = bytes[i + 1];
  return bytes[i] === 0x3c && next !== undefined && isXmlNameStart(next);
}

/**
 * Excel 2003 SpreadsheetML — XML on disk, a workbook in content. Recognised
 * so it can take the SheetJS path instead of a model call over the markup.
 */
function looksLikeSpreadsheetMl(bytes: Uint8Array): boolean {
  const window = bytes.subarray(0, Math.min(bytes.length, 8192));
  return (
    containsAscii(window, "urn:schemas-microsoft-com:office:spreadsheet") ||
    containsAscii(window, 'progid="Excel.Sheet"')
  );
}

/** How much of the file the delimited-text check reads. */
const TEXT_SAMPLE_BYTES = 8192;

/** Candidate CSV delimiters, checked most-common first. */
const TEXT_DELIMITERS = [",", "\t", ";", "|"] as const;

const UTF8_BOM = [0xef, 0xbb, 0xbf];

function isWhitespaceByte(byte: number): boolean {
  return byte === 0x20 || byte === 0x09 || byte === 0x0a || byte === 0x0d;
}

/**
 * Binary guard: a NUL is disqualifying outright, and more than 1% other
 * control characters (allowing tab/LF/CR) means this is not text.
 */
function isBinaryLike(sample: Uint8Array): boolean {
  let controlChars = 0;
  for (const byte of sample) {
    if (byte === 0) return true;
    if (byte < 0x20 && byte !== 0x09 && byte !== 0x0a && byte !== 0x0d) {
      controlChars++;
    }
  }
  return controlChars / sample.length > 0.01;
}

export function isPrintablePlainText(bytes: Uint8Array): boolean {
  const sample = bytes.subarray(0, TEXT_SAMPLE_BYTES);
  if (sample.length === 0) return false;
  return !isBinaryLike(sample);
}

/**
 * Text, but a document rather than a table: JSON and markup both contain
 * plenty of commas and would otherwise pass the delimiter test. Judged by the
 * first meaningful byte being `{`, `[` or `<` — skipping a UTF-8 BOM first,
 * which would otherwise stop the scan before that byte is ever seen.
 */
function startsStructuredDocument(sample: Uint8Array): boolean {
  const start = startsWith(sample, UTF8_BOM) ? UTF8_BOM.length : 0;
  for (const byte of sample.subarray(start)) {
    if (!isWhitespaceByte(byte)) {
      return byte === 0x7b || byte === 0x5b || byte === 0x3c; // { [ <
    }
  }
  return false;
}

/**
 * How many non-blank lines the sample holds, and how many of them carry each
 * candidate delimiter. Computed on raw bytes: the delimiters and newlines are
 * all ASCII, and UTF-8 multibyte sequences never contain ASCII values, so no
 * decoding is needed.
 *
 * When the sample window is full, the trailing line is likely cut mid-row and
 * says nothing about the file's shape — it only counts on a short read.
 */
function delimiterLineStats(sample: Uint8Array): {
  lineCount: number;
  linesWithDelimiter: number[];
} {
  const delimiterBytes = TEXT_DELIMITERS.map((d) => d.charCodeAt(0));
  const linesWithDelimiter = delimiterBytes.map(() => 0);
  const seenOnLine = delimiterBytes.map(() => false);
  let lineCount = 0;
  let lineHasContent = false;

  const endLine = () => {
    if (lineHasContent) {
      lineCount++;
      for (let i = 0; i < delimiterBytes.length; i++) {
        if (seenOnLine[i]) linesWithDelimiter[i]++;
      }
    }
    seenOnLine.fill(false);
    lineHasContent = false;
  };

  for (const byte of sample) {
    if (byte === 0x0a || byte === 0x0d) {
      endLine();
      continue;
    }
    if (byte !== 0x20 && byte !== 0x09) lineHasContent = true;
    const index = delimiterBytes.indexOf(byte);
    if (index !== -1) seenOnLine[index] = true;
  }
  if (sample.length < TEXT_SAMPLE_BYTES) endLine();

  return { lineCount, linesWithDelimiter };
}

/**
 * Whether the leading bytes read as delimited text — a CSV, or a `.txt` of
 * tab/semicolon/pipe-separated rows.
 *
 * Delimited text is the one supported format with no magic number, so unlike
 * everything else in this file it has to be recognised by what the content
 * looks like rather than what it starts with. The bar: printable text, not a
 * JSON/markup document, at least two lines, and one delimiter that appears on
 * most of them. "Most" rather than all, because real pricelist exports open
 * with banner lines ("ACME PRICE LIST 2026") that carry no delimiter.
 *
 * A single-column file fails this check on purpose: with no delimiter anywhere
 * it is indistinguishable from prose, and a one-column pricelist has nothing
 * to extract.
 */
export function looksLikeDelimitedText(bytes: Uint8Array): boolean {
  const sample = bytes.subarray(0, TEXT_SAMPLE_BYTES);
  if (sample.length === 0) return false;
  if (isBinaryLike(sample)) return false;
  if (startsStructuredDocument(sample)) return false;

  const { lineCount, linesWithDelimiter } = delimiterLineStats(sample);
  if (lineCount < 2) return false;
  return linesWithDelimiter.some(
    (count) => count >= Math.ceil(lineCount * 0.6),
  );
}

export function detectTextDelimiter(
  bytes: Uint8Array,
): (typeof TEXT_DELIMITERS)[number] | null {
  if (!looksLikeDelimitedText(bytes)) return null;
  const { lineCount, linesWithDelimiter } = delimiterLineStats(
    bytes.subarray(0, TEXT_SAMPLE_BYTES),
  );
  const threshold = Math.ceil(lineCount * 0.6);
  let best: (typeof TEXT_DELIMITERS)[number] | null = null;
  let bestCount = -1;
  for (let i = 0; i < TEXT_DELIMITERS.length; i++) {
    const count = linesWithDelimiter[i] ?? 0;
    if (count >= threshold && count > bestCount) {
      bestCount = count;
      best = TEXT_DELIMITERS[i] ?? null;
    }
  }
  return best;
}

function isDelimitedTextMimeType(mimeType: string | null | undefined): boolean {
  return (
    mimeType == null ||
    mimeType === "" ||
    mimeType === CSV_MIME_TYPE ||
    mimeType === TEXT_PLAIN_MIME_TYPE ||
    mimeType === "application/csv"
  );
}

export function isPlainTextPricelistFile(
  fileName?: string | null,
  mimeType?: string | null,
): boolean {
  const name = fileName ?? "";
  if (/\.(csv|xlsx|xls|xlsm|xlsb|tsv)$/i.test(name)) return false;
  if (!isDelimitedTextMimeType(mimeType)) return false;
  if (/\.txt$/i.test(name)) return true;
  return mimeType === TEXT_PLAIN_MIME_TYPE;
}

export function pricelistSourceMimeType(
  fileName: string | null | undefined,
  detectedMimeType: string,
): string {
  if (
    detectedMimeType !== CSV_MIME_TYPE &&
    detectedMimeType !== TEXT_PLAIN_MIME_TYPE
  ) {
    return detectedMimeType;
  }
  return isPlainTextPricelistFile(fileName, detectedMimeType)
    ? TEXT_PLAIN_MIME_TYPE
    : detectedMimeType;
}

const ZIP_LOCAL_HEADER = [0x50, 0x4b, 0x03, 0x04];
const ZIP_EMPTY_HEADER = [0x50, 0x4b, 0x05, 0x06];
const ZIP_SPANNED_HEADER = [0x50, 0x4b, 0x07, 0x08];

function isZipContainer(bytes: Uint8Array): boolean {
  return (
    startsWith(bytes, ZIP_LOCAL_HEADER) ||
    startsWith(bytes, ZIP_EMPTY_HEADER) ||
    startsWith(bytes, ZIP_SPANNED_HEADER)
  );
}

function detectBinaryImageType(bytes: Uint8Array): DetectedFileType | null {
  if (startsWith(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) {
    return { kind: "image", mimeType: "image/png" };
  }
  if (startsWith(bytes, [0xff, 0xd8, 0xff])) {
    return { kind: "image", mimeType: "image/jpeg" };
  }
  if (startsWith(bytes, [0x47, 0x49, 0x46, 0x38])) {
    return { kind: "image", mimeType: "image/gif" };
  }
  if (startsWith(bytes, [0x42, 0x4d])) {
    return { kind: "image", mimeType: "image/bmp" };
  }
  if (
    startsWith(bytes, [0x52, 0x49, 0x46, 0x46]) &&
    startsWith(bytes, [0x57, 0x45, 0x42, 0x50], 8)
  ) {
    return { kind: "image", mimeType: "image/webp" };
  }
  return null;
}

function ole2DetectedType(
  kind: Ole2DirectoryKind | null,
): DetectedFileType | null {
  switch (kind) {
    case "email":
      return { kind: "email", mimeType: MSG_MIME_TYPE };
    case "word":
      return { kind: "word", mimeType: DOC_MIME_TYPE };
    case "excel":
      return { kind: "excel", mimeType: XLS_MIME_TYPE };
    default:
      return null;
  }
}

function detectOle2OfficeType(bytes: Uint8Array): DetectedFileType | null {
  return ole2DetectedType(detectOle2DirectoryKindFromBytes(bytes));
}

export async function detectOle2OfficeKind(input: {
  head: Uint8Array;
  tail?: Uint8Array;
  fileSize: number;
  readRange?: ZipRangeReader;
}): Promise<DetectedFileType | null> {
  return ole2DetectedType(await detectOle2DirectoryKind(input));
}

function detectZipOfficeType(bytes: Uint8Array): DetectedFileType | null {
  // `xl/workbook` also matches `xl/workbook.bin`, so the binary form has to
  // win — otherwise an .xlsb is stored and extracted as if it were XLSX.
  if (zipLooksLikeXlsb(bytes)) {
    return { kind: "excel", mimeType: XLSB_MIME_TYPE };
  }
  if (zipIsExcelWorkbook(bytes)) {
    return { kind: "excel", mimeType: XLSX_MIME_TYPE };
  }
  if (zipIsWordDocument(bytes)) {
    return { kind: "word", mimeType: DOCX_MIME_TYPE };
  }
  return null;
}

/**
 * Detect a pricelist file's kind and canonical MIME type from its raw bytes.
 * Returns null when the bytes match no supported type.
 */
export function detectFileType(bytes: Uint8Array): DetectedFileType | null {
  // PDF — "%PDF"
  if (startsWith(bytes, [0x25, 0x50, 0x44, 0x46])) {
    return { kind: "pdf", mimeType: PDF_MIME_TYPE };
  }
  const binaryImage = detectBinaryImageType(bytes);
  if (binaryImage) return binaryImage;
  // OLE2 compound file — Word (.doc) and Excel (.xls) share this magic.
  if (hasOle2Magic(bytes)) {
    return detectOle2OfficeType(bytes);
  }
  // ZIP container — xlsx/xlsm/xlsb, or docx. pptx and plain zip are rejected.
  if (isZipContainer(bytes)) {
    return detectZipOfficeType(bytes);
  }
  // SVG — text-based, so it must be checked before XML / delimited-text
  // fallbacks claim it.
  if (looksLikeSvg(bytes)) {
    return { kind: "image", mimeType: "image/svg+xml" };
  }
  // Excel 2003 SpreadsheetML — XML on disk, a workbook in content. Claimed
  // as Excel so it streams through SheetJS instead of a model call.
  if (looksLikeSpreadsheetMl(bytes)) {
    return { kind: "excel", mimeType: XLS_MIME_TYPE };
  }
  // Generic XML (vendor catalogs, Word 2003 XML). After SVG / SpreadsheetML
  // / HTML, and before delimited-text, which would otherwise reject `<`.
  if (looksLikeXml(bytes)) {
    return { kind: "xml", mimeType: XML_MIME_TYPE };
  }
  // Delimited text — the least specific signature, so always last.
  if (looksLikeDelimitedText(bytes)) {
    return { kind: "csv", mimeType: CSV_MIME_TYPE };
  }
  return null;
}

/**
 * Detect a file's kind from its first {@link PRICELIST_DETECTION_SAMPLE_BYTES}
 * bytes, plus an optional tail of the same size so a ZIP central directory
 * that sits at the end of a large archive is still visible.
 *
 * Differs from {@link detectFileType} in two ZIP cases: a workbook whose
 * `xl/workbook` entry sits beyond the sample (SheetJS-style), and a `.docx`
 * whose `word/document` entry is similarly late. Those are accepted as XLSX or
 * DOCX here from weaker ZIP-path evidence (`word/`); the extraction engine
 * rejects a misclassified archive on first read. `xl/workbook.bin` in the
 * tail wins over that XLSX fallback so the 80 MB XLSB cap still applies.
 */
export function detectFileTypeFromSample(
  sample: Uint8Array,
  tail: Uint8Array = new Uint8Array(),
): DetectedFileType | null {
  const detected = detectFileType(sample);
  if (detected) {
    if (detected.mimeType === XLSX_MIME_TYPE && zipLooksLikeXlsb(tail)) {
      return { kind: "excel", mimeType: XLSB_MIME_TYPE };
    }
    return detected;
  }
  if (isZipContainer(sample)) {
    if (containsAscii(sample, "word/") || containsAscii(tail, "word/")) {
      return { kind: "word", mimeType: DOCX_MIME_TYPE };
    }
    if (zipLooksLikeXlsb(sample) || zipLooksLikeXlsb(tail)) {
      return { kind: "excel", mimeType: XLSB_MIME_TYPE };
    }
    return { kind: "excel", mimeType: XLSX_MIME_TYPE };
  }
  return null;
}

/** File extension (including the dot) for a detected MIME type. */
export function extensionForMimeType(mimeType: string): string {
  switch (mimeType) {
    case PDF_MIME_TYPE:
      return ".pdf";
    case CSV_MIME_TYPE:
      return ".csv";
    case TEXT_PLAIN_MIME_TYPE:
      return ".txt";
    case XLSX_MIME_TYPE:
      return ".xlsx";
    case XLS_MIME_TYPE:
      return ".xls";
    case XLSB_MIME_TYPE:
      return ".xlsb";
    case "image/png":
      return ".png";
    case "image/jpeg":
      return ".jpg";
    case "image/gif":
      return ".gif";
    case "image/webp":
      return ".webp";
    case "image/bmp":
      return ".bmp";
    case "image/svg+xml":
      return ".svg";
    case XML_MIME_TYPE:
    case "text/xml":
      return ".xml";
    case DOC_MIME_TYPE:
      return ".doc";
    case DOCX_MIME_TYPE:
      return ".docx";
    case MSG_MIME_TYPE:
      return ".msg";
    default:
      return ".bin";
  }
}
